// [E-RBAC / S-316] Capability-gated route guard — the React Router counterpart
// to the server-side `requireCapability`. Replaces the role-rank RequireRole:
// access is decided by a CAPABILITY (+ scope), never a role name, so a tenant
// persona (custom role) authorizes in the UI exactly as it does at the API.
//
// Usage:
//   <RequireCapability capability="tenant:manage">
//     <Settings />
//   </RequireCapability>
//
//   // platform-owned resource — only platform reach passes:
//   <RequireCapability capability="platform:manage_tenants" scope="platform">
//     <Tenants />
//   </RequireCapability>
//
//   // tenant-owned resource that excludes platform_admin (no own studio):
//   <RequireCapability capability="classification:manage" scope="tenant">
//     <TenantClassification />
//   </RequireCapability>
//
// Anonymous users are redirected to /login; authenticated-but-unauthorized
// users see the Forbidden fallback (or a custom node). The server re-checks
// every request — this is defense-in-depth + honest UX, not the sole gate.
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { Capability } from "@csai/core/browser";
import { useAuth } from "./AuthProvider";
import { deriveEffectiveSession } from "./activeTenantContext";
import { useActiveTenant } from "./activeTenantContext";
import { sessionAuthorized, type CapabilityScope } from "./capabilities";

export type RequireCapabilityProps = {
  children: ReactNode;
  /** The capability the caller must hold. */
  capability: Capability;
  /** Where it must be held (default "any"). See `sessionAuthorized`. */
  scope?: CapabilityScope;
  /** Render this when authenticated but unauthorized. */
  forbiddenFallback?: ReactNode;
};

export function RequireCapability({
  children,
  capability,
  scope = "any",
  forbiddenFallback,
}: RequireCapabilityProps): JSX.Element {
  const { state } = useAuth();
  const active = useActiveTenant();
  const location = useLocation();

  // [S-637 slice 2] `expired` still carries the last session — keep rendering
  // (the global re-auth modal covers the page) so in-progress work isn't lost
  // to a redirect. Only a truly anonymous/errored state bounces to /login.
  if (state.status !== "authenticated" && state.status !== "expired") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Gate on the EFFECTIVE session for the active tenant, so a per-tenant custom
  // role's capabilities are the ones checked (matches useSession()).
  const session = deriveEffectiveSession(state.session, active);
  if (!sessionAuthorized(session, capability, scope)) {
    return <>{forbiddenFallback ?? <DefaultForbidden />}</>;
  }

  return <>{children}</>;
}

// Bare authentication guard — any signed-in user passes (no capability). For
// routes that authorize a caller's action on THEIR OWN identity (e.g. consenting
// to their own MCP connector), where role/capability is irrelevant.
export function RequireAuth({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const { state } = useAuth();
  const location = useLocation();
  // [S-637 slice 2] `expired` keeps rendering (re-auth modal covers it) so a
  // lapsed session mid-task isn't a lost-work redirect. See RequireCapability.
  if (state.status !== "authenticated" && state.status !== "expired") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function DefaultForbidden(): JSX.Element {
  return (
    <div className="empty" role="status">
      <div className="empty__eyebrow">Forbidden</div>
      <h1 className="empty__title">You don't have access to this page.</h1>
      <p className="empty__lede">
        Your role doesn't grant this action. If you believe this is wrong,
        contact your organization administrator.
      </p>
    </div>
  );
}
